import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { Checkbox, Radio } from "@privacy/ui";
import type { Option as OptionType } from "../../types";

export const Option = ({
  option,
  checked,
  onClick,
  Control,
  disabled,
}: {
  option: OptionType;
  onClick: () => void;
  Control: typeof Radio | typeof Checkbox;
  checked: boolean;
  disabled?: boolean;
}) => {
  const { description, label } = option;

  const glyphProps = { checked, disabled, isReadonly: disabled };

  if (!description) {
    return (
      <Stack
        component="label"
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", cursor: disabled ? "default" : "pointer" }}
      >
        <Control size="small" {...glyphProps} onClick={onClick} />
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>{label}</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      <Stack
        component="button"
        disabled={disabled}
        onClick={onClick}
        type="button"
        sx={{
          "all": "unset",
          "boxSizing": "border-box",
          "p": 3,
          "width": "100%",
          "borderRadius": 1,
          "&:focus-visible": { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
          "border": theme => `1px solid ${theme.palette.grey["400"]}`,
          ...(checked
            ? {
                "borderColor": theme => theme.palette.primary.main,
                "bgcolor": theme => theme.palette.action.selected,
                "&:hover:not(:disabled)": { bgcolor: theme => theme.palette.action.selected, cursor: "pointer" },
              }
            : { "&:hover:not(:disabled)": { bgcolor: theme => theme.palette.action.hover, cursor: "pointer" } }),
        }}
      >
        <Stack sx={{ alignItems: "flex-start", flexDirection: "row", gap: 2 }}>
          <Control size="small" {...glyphProps} sx={{ p: 0 }} />
          <Stack spacing={0.75}>
            <Typography
              component="span"
              sx={{ pt: "1px", fontWeight: 600, lineHeight: 1, fontSize: theme => theme.typography.pxToRem(14) }}
            >
              {label}
            </Typography>
            {description && (
              <Typography
                sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.secondary }}
              >
                {description}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};
